/**
 * LeagueRegistrationPage — /league/:leagueId/registration
 * ─────────────────────────────────────────────────────────────────
 * v2.5.133 — Phase 9A.
 *
 * The manager configures one registration setup per season (the
 * default), optionally with per-division overrides. This page is
 * a season picker + three-section editor:
 *
 *   1. Config — name, intro, opening window, fee, parent-consent
 *      threshold, published flag
 *   2. Form fields — drag-to-reorder list of custom fields
 *      (text/number/date/select/etc) with per-field validation
 *   3. Waiver sections — multi-section waiver editor with
 *      markdown body + acknowledgment text + required toggle.
 *      Editing a section with existing acknowledgments creates
 *      a new version (preserving the old text forever).
 *
 * The public sign-up form is PublicRegisterPage (/l/:slug/register).
 *
 * 2026-09-28 — Submissions got a detail view (every answer, each signed
 * waiver with version / time / IP / signature, the payment trail, a
 * printable signed-waiver record), a CSV export, a status summary with
 * filters, and ?season=<id> deep links (Admin → Demo links here).
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Save, Plus, Trash2, ClipboardList, FileText, ListChecks, AlertCircle, History,
  Eye, Download, Printer, Search, Lock, X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api.js';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
import MarkdownEditor from '../../components/MarkdownEditor.jsx';
import LeagueSubNav from './LeagueSubNav.jsx';

export default function LeagueRegistrationPage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canEdit = isManager || can('manage_registration');
  // Phase C (league gap-closure) — finance actions (Stripe Connect,
  // mark-paid, refunds) use the separate manage_finances key. A
  // finance-only administrator can now reach this page for the
  // registrations list + payments card without form-builder rights.
  const canFinance = isManager || can('manage_finances');

  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonIdState] = useState(null);
  const [seasonsLoading, setSeasonsLoading] = useState(true);
  // 2026-09-28 — ?season=<id> selects a season (and follows the picker).
  const [searchParams, setSearchParams] = useSearchParams();
  const setSelectedSeasonId = (id) => {
    setSelectedSeasonIdState(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('season', id); else next.delete('season');
    setSearchParams(next, { replace: true });
  };

  // Load seasons for the picker. We always operate on the
  // season-default config in 9A; division-override editing is a
  // future enhancement that this page is shaped to accept.
  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    api.get(`/leagues/${leagueId}/seasons`)
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.seasons || [];
        setSeasons(list);
        // ?season=<id> wins; otherwise the currently-active season if
        // there is one, otherwise the most recently created.
        const wanted = searchParams.get('season');
        const fromUrl = wanted && list.find(s => s.id === wanted);
        const active = list.find(s => s.status === 'active');
        const fallback = list[0] || null;
        setSelectedSeasonIdState((fromUrl || active || fallback)?.id || null);
      })
      .catch(() => { if (!cancelled) setSeasons([]); })
      .finally(() => { if (!cancelled) setSeasonsLoading(false); });
    return () => { cancelled = true; };
  }, [leagueId]);

  if (roleLoading || seasonsLoading) {
    return (
      <div className="container">
        <LeagueSubNav league={null} />
        <div className="card">Loading…</div>
      </div>
    );
  }

  if (!canEdit && !canFinance) {
    return (
      <div className="container">
        <LeagueSubNav league={null} />
        <div className="card">
          You do not have permission to manage registration for this league.
        </div>
      </div>
    );
  }

  if (seasons.length === 0) {
    return (
      <div className="container">
        <LeagueSubNav league={null} />
        <div className="card">
          <h1 className="section-title">Registration</h1>
          <p>Create a season first to configure registration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <LeagueSubNav league={null} />

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <ClipboardList size={20} color="var(--ice)" />
          <h1 className="section-title" style={{ margin: 0 }}>Registration</h1>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
          Configure how people register for a season — form fields, waivers, opening window and fee — and
          work through the submissions. Once a season's registration is published and open, players sign up
          on your league website's Register page; next season's registration can open while the current
          season is still being played.
        </p>

        <label style={{ fontSize: 12, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>
          Season
        </label>
        <select
          value={selectedSeasonId || ''}
          onChange={(e) => setSelectedSeasonId(e.target.value || null)}
          style={{ ...selectStyle, minWidth: 220 }}>
          {seasons.map(s => (
            <option key={s.id} value={s.id}>
              {s.name}{s.status === 'active' ? ' · current' : s.status === 'draft' ? ' · upcoming' : s.status === 'completed' ? ' · finished' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Phase C — league payout account. League-level (not per
          season); registration checkout routes fees here. */}
      {canFinance && <LeaguePaymentsCard leagueId={leagueId} />}

      {selectedSeasonId && (
        <SeasonRegistrationEditor
          leagueId={leagueId}
          seasonId={selectedSeasonId}
          seasonName={seasons.find(s => s.id === selectedSeasonId)?.name || ''}
          canEdit={canEdit}
          canFinance={canFinance}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Per-season editor — config + fields + waivers
// ─────────────────────────────────────────────────────────────────
function SeasonRegistrationEditor({ leagueId, seasonId, seasonName, canEdit, canFinance }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reload config whenever season changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/leagues/${leagueId}/seasons/${seasonId}/registration`)
      .then(({ data }) => { if (!cancelled) setConfig(data?.config || null); })
      .catch(() => { if (!cancelled) setConfig(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leagueId, seasonId]);

  if (loading) {
    return <div className="card">Loading registration config…</div>;
  }

  if (!config) {
    return (
      <ConfigCreate
        leagueId={leagueId}
        seasonId={seasonId}
        canEdit={canEdit}
        onCreated={setConfig}
      />
    );
  }

  return (
    <>
      <ConfigEditor
        leagueId={leagueId}
        seasonId={seasonId}
        config={config}
        canEdit={canEdit}
        onUpdated={setConfig}
      />
      <FormFieldsEditor
        leagueId={leagueId}
        seasonId={seasonId}
        configId={config.id}
        canEdit={canEdit}
      />
      <WaiverSectionsEditor
        leagueId={leagueId}
        seasonId={seasonId}
        configId={config.id}
        canEdit={canEdit}
      />
      {/* Phase C — submissions pipeline: confirm → pool, mark-paid,
          refund, cancel. */}
      <RegistrationsList
        leagueId={leagueId}
        seasonId={seasonId}
        seasonName={seasonName}
        configId={config.id}
        configName={config.name}
        canEdit={canEdit}
        canFinance={canFinance}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// ConfigCreate — first-time setup
// ─────────────────────────────────────────────────────────────────
function ConfigCreate({ leagueId, seasonId, canEdit, onCreated }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function create() {
    if (!canEdit) return;
    if (name.trim().length === 0) {
      toast.error('Give the registration a name');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put(
        `/leagues/${leagueId}/seasons/${seasonId}/registration`,
        { name: name.trim() }
      );
      onCreated(data.config);
      toast.success('Registration created');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to create registration');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2 className="section-title text-lg" style={{ marginBottom: 8 }}>Create registration</h2>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
        No registration config exists for this season yet. Give it a name to start.
      </p>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. 2026/27 House League Registration"
        maxLength={160}
        style={inputStyle}
      />
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn-ice" onClick={create} disabled={busy || !name.trim()}>
          {busy ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ConfigEditor — main config card
// ─────────────────────────────────────────────────────────────────
function ConfigEditor({ leagueId, seasonId, config, canEdit, onUpdated }) {
  const [draft, setDraft] = useState({
    name: config.name || '',
    intro_markdown: config.intro_markdown || '',
    opens_at: config.opens_at ? config.opens_at.slice(0, 16) : '',
    closes_at: config.closes_at ? config.closes_at.slice(0, 16) : '',
    max_registrants: config.max_registrants ?? '',
    base_fee_cents: config.base_fee_cents ?? '',
    currency: config.currency || 'CAD',
    requires_account: !!config.requires_account,
    parent_consent_under_age: config.parent_consent_under_age ?? 18,
    is_published: !!config.is_published,
  });
  const [saving, setSaving] = useState(false);

  // Resync if the parent config changes (e.g. after another card
  // saves something that re-fetches).
  useEffect(() => {
    setDraft({
      name: config.name || '',
      intro_markdown: config.intro_markdown || '',
      opens_at: config.opens_at ? config.opens_at.slice(0, 16) : '',
      closes_at: config.closes_at ? config.closes_at.slice(0, 16) : '',
      max_registrants: config.max_registrants ?? '',
      base_fee_cents: config.base_fee_cents ?? '',
      currency: config.currency || 'CAD',
      requires_account: !!config.requires_account,
      parent_consent_under_age: config.parent_consent_under_age ?? 18,
      is_published: !!config.is_published,
    });
  }, [config]);

  async function save() {
    if (!canEdit) return;
    if (draft.name.trim().length === 0) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        intro_markdown: draft.intro_markdown || null,
        // datetime-local inputs return 'YYYY-MM-DDTHH:MM' (no TZ).
        // Browser interprets in local TZ; sending as-is lets the
        // server treat it as a local time. For 9A we just append
        // ':00Z' if blank? No — leave blank as null. Otherwise
        // append seconds to make a valid ISO string interpreted
        // as local time. The Date constructor will normalize on
        // the backend.
        opens_at: draft.opens_at ? draft.opens_at : null,
        closes_at: draft.closes_at ? draft.closes_at : null,
        max_registrants: draft.max_registrants === '' ? null : Number(draft.max_registrants),
        base_fee_cents: draft.base_fee_cents === '' ? null : Number(draft.base_fee_cents),
        currency: draft.currency,
        requires_account: draft.requires_account,
        parent_consent_under_age: Number(draft.parent_consent_under_age),
        is_published: draft.is_published,
      };
      const { data } = await api.put(
        `/leagues/${leagueId}/seasons/${seasonId}/registration`,
        payload
      );
      onUpdated(data.config);
      toast.success('Config saved');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2 className="section-title text-lg" style={{ marginBottom: 8 }}>Setup</h2>

      <Field label="Name" hint="Internal label. Shown to admins; not on the public form.">
        <input
          type="text" value={draft.name} maxLength={160}
          onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
          disabled={!canEdit}
          style={inputStyle}
        />
      </Field>

      <Field label="Intro" hint="Markdown. Shown to registrants above the form. Optional.">
        <MarkdownEditor
          value={draft.intro_markdown}
          onChange={(v) => setDraft(d => ({ ...d, intro_markdown: v }))}
          placeholder="Welcome! Here's what you need to know before registering…"
          disabled={!canEdit}
          rows={6}
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <Field label="Opens at" hint="Optional. Form is unavailable to the public before this time.">
          <input
            type="datetime-local"
            value={draft.opens_at}
            onChange={(e) => setDraft(d => ({ ...d, opens_at: e.target.value }))}
            disabled={!canEdit}
            style={inputStyle}
          />
        </Field>
        <Field label="Closes at" hint="Optional. After this time the form locks.">
          <input
            type="datetime-local"
            value={draft.closes_at}
            onChange={(e) => setDraft(d => ({ ...d, closes_at: e.target.value }))}
            disabled={!canEdit}
            style={inputStyle}
          />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 6 }}>
        <Field label="Max registrants" hint="Optional cap on confirmed registrations.">
          <input
            type="number" min="1"
            value={draft.max_registrants}
            onChange={(e) => setDraft(d => ({ ...d, max_registrants: e.target.value }))}
            disabled={!canEdit}
            style={inputStyle}
          />
        </Field>
        <Field label="Base fee (cents)" hint="Charged at registration (e.g. 27500 = $275.00). Leave empty or 0 for no fee.">
          <input
            type="number" min="0"
            value={draft.base_fee_cents}
            onChange={(e) => setDraft(d => ({ ...d, base_fee_cents: e.target.value }))}
            disabled={!canEdit}
            style={inputStyle}
          />
        </Field>
        <Field label="Currency">
          <input
            type="text" maxLength={3}
            value={draft.currency}
            onChange={(e) => setDraft(d => ({ ...d, currency: e.target.value.toUpperCase() }))}
            disabled={!canEdit}
            style={inputStyle}
          />
        </Field>
        <Field label="Parent-consent under age" hint="Below this age, the parent's account is required to register.">
          <input
            type="number" min="0" max="100"
            value={draft.parent_consent_under_age}
            onChange={(e) => setDraft(d => ({ ...d, parent_consent_under_age: e.target.value }))}
            disabled={!canEdit}
            style={inputStyle}
          />
        </Field>
      </div>

      <div style={{ marginTop: 14, padding: 12, background: 'var(--surface-2)', borderRadius: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canEdit ? 'pointer' : 'not-allowed' }}>
          <input
            type="checkbox"
            checked={draft.is_published}
            onChange={(e) => setDraft(d => ({ ...d, is_published: e.target.checked }))}
            disabled={!canEdit}
          />
          <strong>Published</strong>
          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
            Until this is checked, the form is not visible to the public (even if opens_at has passed).
          </span>
        </label>
      </div>

      {canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <button
            type="button"
            className="btn-ice"
            onClick={save}
            disabled={saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Save size={14} />
            {saving ? 'Saving…' : 'Save setup'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FormFieldsEditor — the form builder
// ─────────────────────────────────────────────────────────────────
const FIELD_TYPES = [
  { value: 'text',        label: 'Short text' },
  { value: 'textarea',    label: 'Long text' },
  { value: 'number',      label: 'Number' },
  { value: 'date',        label: 'Date' },
  { value: 'email',       label: 'Email' },
  { value: 'phone',       label: 'Phone' },
  { value: 'checkbox',    label: 'Checkbox' },
  { value: 'select',      label: 'Dropdown (single)' },
  { value: 'multiselect', label: 'Dropdown (multiple)' },
  // 'file' deferred to a later phase — needs upload plumbing.
];

function FormFieldsEditor({ leagueId, seasonId, configId, canEdit }) {
  const [fields, setFields] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const base = `/leagues/${leagueId}/seasons/${seasonId}/registration/${configId}/fields`;

  useEffect(() => {
    let cancelled = false;
    api.get(base)
      .then(({ data }) => { if (!cancelled) setFields(data?.fields || []); })
      .catch(() => { if (!cancelled) setFields([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]);

  async function refresh() {
    const { data } = await api.get(base);
    setFields(data?.fields || []);
  }

  async function deleteField(fieldId, label) {
    if (!canEdit) return;
    if (!window.confirm(`Delete the "${label}" field? Any draft form values for this field will be lost.`)) return;
    try {
      await api.delete(`${base}/${fieldId}`);
      await refresh();
      toast.success('Field deleted');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete field');
    }
  }

  // Reorder via move up/down (avoids needing a drag-and-drop dep).
  async function move(fieldId, direction) {
    if (!canEdit || !fields) return;
    const idx = fields.findIndex(f => f.id === fieldId);
    if (idx < 0) return;
    const j = idx + (direction === 'up' ? -1 : 1);
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[idx], next[j]] = [next[j], next[idx]];
    setFields(next); // optimistic
    try {
      await api.put(`${base}/reorder`, { order: next.map(f => f.id) });
    } catch (err) {
      toast.error('Reorder failed');
      await refresh();
    }
  }

  if (fields === null) return <div className="card">Loading fields…</div>;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <FileText size={18} color="var(--ice)" />
        <h2 className="section-title text-lg" style={{ margin: 0 }}>Form fields</h2>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
        Build the registration form. Add fields the parent/registrant will fill in.
        Reorder with the up/down arrows.
      </p>

      {fields.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text-2)' }}>No fields yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {fields.map((f, idx) => (
            <FieldRow
              key={f.id}
              field={f}
              isFirst={idx === 0}
              isLast={idx === fields.length - 1}
              canEdit={canEdit}
              isEditing={editingId === f.id}
              onEdit={() => setEditingId(f.id)}
              onCancel={() => setEditingId(null)}
              onSaved={async () => { setEditingId(null); await refresh(); }}
              onDelete={() => deleteField(f.id, f.label)}
              onMoveUp={() => move(f.id, 'up')}
              onMoveDown={() => move(f.id, 'down')}
              base={base}
            />
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{ marginTop: 12 }}>
          {!adding ? (
            <button type="button" className="btn-ghost" onClick={() => setAdding(true)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={14} />
              Add field
            </button>
          ) : (
            <NewFieldForm
              base={base}
              onCancel={() => setAdding(false)}
              onAdded={async () => { setAdding(false); await refresh(); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function FieldRow({ field, isFirst, isLast, canEdit, isEditing, onEdit, onCancel, onSaved, onDelete, onMoveUp, onMoveDown, base }) {
  if (isEditing) {
    return (
      <EditFieldForm
        base={base}
        field={field}
        onCancel={onCancel}
        onSaved={onSaved}
      />
    );
  }
  const typeLabel = FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type;
  return (
    <div style={fieldRowStyle}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, color: 'var(--text-3)' }}>
        <button type="button" onClick={onMoveUp} disabled={!canEdit || isFirst}
                style={miniBtnStyle(canEdit && !isFirst)} aria-label="Move up">▲</button>
        <button type="button" onClick={onMoveDown} disabled={!canEdit || isLast}
                style={miniBtnStyle(canEdit && !isLast)} aria-label="Move down">▼</button>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {field.label}
          {field.is_required && <span style={{ color: 'var(--penalty)', marginLeft: 4 }}>*</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
          {typeLabel} · key=<code style={codeStyle}>{field.field_key}</code>
          {field.is_sensitive && <> · <Lock size={10} style={{ verticalAlign: -1 }} /> encrypted</>}
          {field.help_text && <> · {field.help_text}</>}
        </div>
        {Array.isArray(field.options_json) && field.options_json.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>
            Choices: {field.options_json.map(o => o.label).join(' · ')}
          </div>
        )}
        {(field.field_type === 'select' || field.field_type === 'multiselect')
          && !(Array.isArray(field.options_json) && field.options_json.length) && (
          <div style={{ fontSize: 11, color: 'var(--puck, #f5a623)', marginTop: 3 }}>
            No choices yet — edit this field to add them.
          </div>
        )}
      </div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" className="btn-ghost" onClick={onEdit}
                  style={{ fontSize: 12, padding: '4px 10px' }}>
            Edit
          </button>
          <button type="button" className="btn-ghost" onClick={onDelete}
                  style={{ fontSize: 12, padding: '4px 8px', color: 'var(--penalty)' }}
                  title="Delete">
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function NewFieldForm({ base, onCancel, onAdded }) {
  const [field_key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [field_type, setType] = useState('text');
  const [is_required, setRequired] = useState(false);
  const [help_text, setHelp] = useState('');
  // 2026-09-28 — dropdown choices, one per line.
  const [options_text, setOptionsText] = useState('');
  // v2.5.134 — is_sensitive defaults derived from field_type. We
  // track an explicit user-touched flag so changing the type
  // re-suggests the default until the user has expressed an
  // opinion.
  const [is_sensitive, setSensitive] = useState(true);
  const [sensitiveTouched, setSensitiveTouched] = useState(false);
  useEffect(() => {
    if (sensitiveTouched) return;
    const sensitiveByDefault = ['text', 'textarea', 'email', 'phone'].includes(field_type);
    setSensitive(sensitiveByDefault);
  }, [field_type, sensitiveTouched]);
  const [busy, setBusy] = useState(false);

  // Auto-derive field_key from label as user types — until they
  // manually edit the key, in which case stop overwriting.
  const [keyTouched, setKeyTouched] = useState(false);
  useEffect(() => {
    if (keyTouched) return;
    setKey(deriveKey(label));
  }, [label, keyTouched]);

  async function submit() {
    if (!label.trim() || !field_key.trim()) {
      toast.error('Label and key are required');
      return;
    }
    const isChoice = field_type === 'select' || field_type === 'multiselect';
    const options = isChoice ? parseOptions(options_text) : null;
    if (isChoice && options.length === 0) {
      toast.error('Add at least one choice (one per line)');
      return;
    }
    setBusy(true);
    try {
      await api.post(base, {
        field_key: field_key.trim(),
        label: label.trim(),
        field_type,
        is_required,
        is_sensitive,
        help_text: help_text.trim() || null,
        ...(isChoice ? { options_json: options } : {}),
      });
      await onAdded();
      toast.success('Field added');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to add field');
    } finally {
      setBusy(false);
    }
  }

  return (
    <FieldFormShell title="New field" onCancel={onCancel} onSubmit={submit} busy={busy} submitLabel="Add">
      <FieldFormBody
        label={label} setLabel={setLabel}
        field_key={field_key} setKey={(v) => { setKey(v); setKeyTouched(true); }}
        field_type={field_type} setType={setType}
        is_required={is_required} setRequired={setRequired}
        is_sensitive={is_sensitive} setSensitive={(v) => { setSensitive(v); setSensitiveTouched(true); }}
        help_text={help_text} setHelp={setHelp}
        options_text={options_text} setOptionsText={setOptionsText}
      />
    </FieldFormShell>
  );
}

function EditFieldForm({ base, field, onCancel, onSaved }) {
  const [label, setLabel] = useState(field.label);
  const [field_key, setKey] = useState(field.field_key);
  const [field_type, setType] = useState(field.field_type);
  const [is_required, setRequired] = useState(!!field.is_required);
  // v2.5.134 — for edits, we always honor the saved value rather
  // than re-deriving from field_type; the manager already made a
  // choice on this field.
  const [is_sensitive, setSensitive] = useState(!!field.is_sensitive);
  const [help_text, setHelp] = useState(field.help_text || '');
  const [options_text, setOptionsText] = useState(
    Array.isArray(field.options_json) ? field.options_json.map(o => o.label).join('\n') : '');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!label.trim()) {
      toast.error('Label is required');
      return;
    }
    const isChoice = field_type === 'select' || field_type === 'multiselect';
    // Keep the stored value of every choice whose wording is unchanged,
    // so answers already given still match.
    const options = isChoice ? parseOptions(options_text, field.options_json) : null;
    if (isChoice && options.length === 0) {
      toast.error('Add at least one choice (one per line)');
      return;
    }
    setBusy(true);
    try {
      await api.patch(`${base}/${field.id}`, {
        label: label.trim(),
        field_key: field_key.trim(),
        field_type,
        is_required,
        is_sensitive,
        help_text: help_text.trim() || null,
        options_json: options,
      });
      await onSaved();
      toast.success('Field updated');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update field');
    } finally {
      setBusy(false);
    }
  }

  return (
    <FieldFormShell title="Edit field" onCancel={onCancel} onSubmit={submit} busy={busy} submitLabel="Save">
      <FieldFormBody
        label={label} setLabel={setLabel}
        field_key={field_key} setKey={setKey}
        field_type={field_type} setType={setType}
        is_required={is_required} setRequired={setRequired}
        is_sensitive={is_sensitive} setSensitive={setSensitive}
        help_text={help_text} setHelp={setHelp}
        options_text={options_text} setOptionsText={setOptionsText}
      />
    </FieldFormShell>
  );
}

// 2026-09-28 — "one choice per line" → [{ value, label }]. Existing
// choices keep their stored value when the wording is unchanged.
function parseOptions(text, existing = []) {
  const byLabel = new Map((Array.isArray(existing) ? existing : [])
    .map(o => [String(o.label || '').trim().toLowerCase(), String(o.value)]));
  const used = new Set();
  const out = [];
  for (const raw of String(text || '').split('\n')) {
    const label = raw.trim().slice(0, 200);
    if (!label) continue;
    const base = byLabel.get(label.toLowerCase())
      || label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60)
      || `option_${out.length + 1}`;
    let v = base;
    let n = 2;
    while (used.has(v)) v = `${base}_${n++}`;
    used.add(v);
    out.push({ value: v, label });
  }
  return out.slice(0, 100);
}

function FieldFormShell({ title, children, onCancel, onSubmit, busy, submitLabel }) {
  return (
    <div style={{ padding: 14, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{title}</div>
      {children}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn-ice" onClick={onSubmit} disabled={busy}>
          {busy ? '…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

function FieldFormBody({ label, setLabel, field_key, setKey, field_type, setType, is_required, setRequired, help_text, setHelp,
                         is_sensitive, setSensitive, options_text, setOptionsText }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <label style={miniLabelStyle}>Label</label>
        <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200} style={inputStyle} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <div>
          <label style={miniLabelStyle}>Field key</label>
          <input type="text" value={field_key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                 maxLength={64} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
        </div>
        <div>
          <label style={miniLabelStyle}>Type</label>
          <select value={field_type} onChange={(e) => setType(e.target.value)} style={selectStyle}>
            {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label style={miniLabelStyle}>Help text (optional)</label>
        <input type="text" value={help_text} onChange={(e) => setHelp(e.target.value)} style={inputStyle} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
        <input type="checkbox" checked={is_required} onChange={(e) => setRequired(e.target.checked)} />
        Required
      </label>
      {['text', 'textarea', 'email', 'phone'].includes(field_type) && setSensitive && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={!!is_sensitive} onChange={(e) => setSensitive(e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            Sensitive — store encrypted
            <span style={{ display: 'block', fontSize: 11, color: 'var(--text-3)' }}>
              For health notes, emergency contacts and the like. Only people who manage registration can read it.
            </span>
          </span>
        </label>
      )}
      {(field_type === 'select' || field_type === 'multiselect') && setOptionsText && (
        <div>
          <label style={miniLabelStyle}>Choices — one per line</label>
          <textarea value={options_text || ''} onChange={(e) => setOptionsText(e.target.value)} rows={5}
                    placeholder={'Small\nMedium\nLarge\nX-Large'}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          <p style={{ fontSize: 11, color: 'var(--text-3)', margin: '4px 0 0' }}>
            {field_type === 'multiselect' ? 'People can tick several.' : 'People pick one.'}
            {' '}Renaming a choice later keeps answers already given only if the wording stays the same.
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// WaiverSectionsEditor
// ─────────────────────────────────────────────────────────────────
function WaiverSectionsEditor({ leagueId, seasonId, configId, canEdit }) {
  const [sections, setSections] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const base = `/leagues/${leagueId}/seasons/${seasonId}/registration/${configId}/waiver-sections`;

  useEffect(() => {
    let cancelled = false;
    api.get(base)
      .then(({ data }) => { if (!cancelled) setSections(data?.sections || []); })
      .catch(() => { if (!cancelled) setSections([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]);

  async function refresh() {
    const { data } = await api.get(base);
    setSections(data?.sections || []);
  }

  async function deleteSection(sectionId, title) {
    if (!canEdit) return;
    if (!window.confirm(`Delete the "${title}" section?`)) return;
    try {
      const { data } = await api.delete(`${base}/${sectionId}`);
      await refresh();
      toast.success(data.mode === 'archived'
        ? 'Section archived (acknowledgments preserved)'
        : 'Section deleted');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete section');
    }
  }

  if (sections === null) return <div className="card">Loading waiver sections…</div>;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <ListChecks size={18} color="var(--ice)" />
        <h2 className="section-title text-lg" style={{ margin: 0 }}>Waiver sections</h2>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
        Multi-section waivers. Each section gets its own acknowledgment checkbox on the registration form.
        Editing a section after someone has signed it creates a new version — the original text is preserved
        forever for legal audit.
      </p>

      {sections.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text-2)' }}>No sections yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sections.map((s) => (
            <SectionRow
              key={s.id}
              section={s}
              canEdit={canEdit}
              isEditing={editingId === s.id}
              onEdit={() => setEditingId(s.id)}
              onCancel={() => setEditingId(null)}
              onSaved={async () => { setEditingId(null); await refresh(); }}
              onDelete={() => deleteSection(s.id, s.title)}
              base={base}
            />
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{ marginTop: 12 }}>
          {!adding ? (
            <button type="button" className="btn-ghost" onClick={() => setAdding(true)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={14} />
              Add section
            </button>
          ) : (
            <NewSectionForm
              base={base}
              onCancel={() => setAdding(false)}
              onAdded={async () => { setAdding(false); await refresh(); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SectionRow({ section, canEdit, isEditing, onEdit, onCancel, onSaved, onDelete, base }) {
  if (isEditing) {
    return (
      <EditSectionForm
        base={base}
        section={section}
        onCancel={onCancel}
        onSaved={onSaved}
      />
    );
  }
  return (
    <div style={fieldRowStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {section.title}
          {section.is_required && <span style={{ color: 'var(--penalty)', marginLeft: 4 }}>*</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span><History size={10} style={{ verticalAlign: 'middle' }} /> v{section.version}</span>
          <span>"{section.acknowledgment_text}"</span>
        </div>
      </div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" className="btn-ghost" onClick={onEdit}
                  style={{ fontSize: 12, padding: '4px 10px' }}>
            Edit
          </button>
          <button type="button" className="btn-ghost" onClick={onDelete}
                  style={{ fontSize: 12, padding: '4px 8px', color: 'var(--penalty)' }}>
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function NewSectionForm({ base, onCancel, onAdded }) {
  const [title, setTitle] = useState('');
  const [body_markdown, setBody] = useState('');
  const [acknowledgment_text, setAck] = useState('');
  const [is_required, setRequired] = useState(true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!title.trim() || !body_markdown.trim() || !acknowledgment_text.trim()) {
      toast.error('Title, body, and acknowledgment text are required');
      return;
    }
    setBusy(true);
    try {
      await api.post(base, {
        title: title.trim(),
        body_markdown,
        acknowledgment_text: acknowledgment_text.trim(),
        is_required,
      });
      await onAdded();
      toast.success('Section added');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to add section');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SectionFormShell title="New section" onCancel={onCancel} onSubmit={submit} busy={busy} submitLabel="Add">
      <SectionFormBody
        title={title} setTitle={setTitle}
        body_markdown={body_markdown} setBody={setBody}
        acknowledgment_text={acknowledgment_text} setAck={setAck}
        is_required={is_required} setRequired={setRequired}
      />
    </SectionFormShell>
  );
}

function EditSectionForm({ base, section, onCancel, onSaved }) {
  const [title, setTitle] = useState(section.title);
  const [body_markdown, setBody] = useState(section.body_markdown);
  const [acknowledgment_text, setAck] = useState(section.acknowledgment_text);
  const [is_required, setRequired] = useState(!!section.is_required);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!title.trim() || !body_markdown.trim() || !acknowledgment_text.trim()) {
      toast.error('Title, body, and acknowledgment text are required');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put(`${base}/${section.id}`, {
        title: title.trim(),
        body_markdown,
        acknowledgment_text: acknowledgment_text.trim(),
        is_required,
      });
      await onSaved();
      if (data.mode === 'superseded') {
        toast.success(`New version v${data.section.version} created — old acknowledgments preserved`);
      } else {
        toast.success('Section updated');
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update section');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SectionFormShell title={`Edit section — v${section.version}`} onCancel={onCancel} onSubmit={submit} busy={busy} submitLabel="Save">
      <SectionFormBody
        title={title} setTitle={setTitle}
        body_markdown={body_markdown} setBody={setBody}
        acknowledgment_text={acknowledgment_text} setAck={setAck}
        is_required={is_required} setRequired={setRequired}
      />
      <div style={{ marginTop: 8, padding: 8, background: 'var(--surface-2)', borderRadius: 6, fontSize: 11, color: 'var(--text-2)' }}>
        <AlertCircle size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        If anyone has already acknowledged this section, saving will create a new version. Existing acknowledgments stay linked to the original text.
      </div>
    </SectionFormShell>
  );
}

function SectionFormShell({ title, children, onCancel, onSubmit, busy, submitLabel }) {
  return (
    <div style={{ padding: 14, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{title}</div>
      {children}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn-ice" onClick={onSubmit} disabled={busy}>
          {busy ? '…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

function SectionFormBody({ title, setTitle, body_markdown, setBody, acknowledgment_text, setAck, is_required, setRequired }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <label style={miniLabelStyle}>Title</label>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} style={inputStyle} />
      </div>
      <div>
        <label style={miniLabelStyle}>Body (markdown)</label>
        <MarkdownEditor
          value={body_markdown}
          onChange={setBody}
          placeholder="The waiver text the registrant must read…"
          rows={10}
        />
      </div>
      <div>
        <label style={miniLabelStyle}>Acknowledgment checkbox text</label>
        <input type="text" value={acknowledgment_text} onChange={(e) => setAck(e.target.value)} maxLength={500}
               placeholder="I have read and agree to the waiver above" style={inputStyle} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
        <input type="checkbox" checked={is_required} onChange={(e) => setRequired(e.target.checked)} />
        Required (blocks submission if unchecked)
      </label>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Helpers + shared styles
// ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────
// Phase C (league gap-closure) — Stripe Connect payout card
// ─────────────────────────────────────────────────────────────────
function LeaguePaymentsCard({ leagueId }) {
  const [status, setStatus] = useState(null); // null=loading, {connected,...}
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/leagues/${leagueId}/stripe-connect/status`);
      setStatus(data);
    } catch {
      setStatus({ error: true });
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId]);

  // Returning from Stripe onboarding (?stripe=return|refresh) —
  // force a live refresh so the flags are current.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('stripe');
    if (!p) return;
    api.post(`/leagues/${leagueId}/stripe-connect/refresh`)
      .then(({ data }) => setStatus(data))
      .catch(() => {});
    // eslint-disable-next-line
  }, [leagueId]);

  async function onboard() {
    setBusy(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/stripe-connect/onboard`, {});
      if (data?.url) { window.location.assign(data.url); return; }
      toast.error('Could not start onboarding');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not start onboarding');
    } finally {
      setBusy(false);
    }
  }
  async function openDashboard() {
    setBusy(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/stripe-connect/dashboard`);
      if (data?.url) window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not open the Stripe dashboard');
    } finally {
      setBusy(false);
    }
  }

  const ready = status?.connected && status?.chargesEnabled;
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2 className="section-title" style={{ fontSize: 16, marginBottom: 8 }}>Payments</h2>
      {status === null && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Checking Stripe status…</div>}
      {status?.error && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Could not load Stripe status.</div>}
      {/* 2026-09-28 — demo leagues never connect Stripe. */}
      {status?.demo && (
        <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>
          <strong style={{ color: '#fbbf24' }}>Demo league:</strong> card payments on the registration page are
          simulated, so there is no Stripe account to connect. In a real league this is where the league connects
          its own Stripe account — registration fees are paid by card and land directly in the league&apos;s Stripe
          balance; cash and e-transfers are recorded with &quot;Mark paid&quot;.
        </p>
      )}
      {status && !status.error && !status.demo && !status.connected && (
        <>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 10 }}>
            Connect a Stripe account so registrants can pay their fee online.
            Fees land directly in the league's Stripe balance. Until then, registrants
            arrange payment with you and you use "Mark paid" below.
          </p>
          <button className="btn-ice" disabled={busy} onClick={onboard}>
            {busy ? 'Starting…' : 'Connect Stripe account'}
          </button>
        </>
      )}
      {status?.connected && (
        <div style={{ fontSize: 13 }}>
          <div style={{ marginBottom: 8 }}>
            {ready
              ? <span style={{ color: 'var(--goal, #2ecc71)' }}>✓ Stripe connected — online registration payments are live.</span>
              : <span style={{ color: 'var(--puck, #f5a623)' }}>⚠ Stripe account connected but not fully enabled yet — finish onboarding to accept payments.</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!ready && (
              <button className="btn-ice" disabled={busy} onClick={onboard}>
                {busy ? 'Working…' : 'Resume onboarding'}
              </button>
            )}
            <button className="btn-ghost" disabled={busy} onClick={openDashboard}>Stripe dashboard</button>
            <button className="btn-ghost" disabled={busy} onClick={async () => {
              setBusy(true);
              try { const { data } = await api.post(`/leagues/${leagueId}/stripe-connect/refresh`); setStatus(data); }
              catch { toast.error('Refresh failed'); }
              finally { setBusy(false); }
            }}>Refresh status</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Phase C — submissions list + pipeline actions
// ─────────────────────────────────────────────────────────────────
function RegistrationsList({ leagueId, seasonId, seasonName, configId, configName, canEdit, canFinance }) {
  const base = `/leagues/${leagueId}/seasons/${seasonId}/registration/${configId}`;
  const [regs, setRegs] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [viewId, setViewId] = useState(null);
  const [exporting, setExporting] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`${base}/registrations`);
      setRegs(data?.registrations || []);
    } catch {
      setRegs([]);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [configId]);

  async function act(reg, action, body = {}) {
    setBusyId(reg.id);
    try {
      const { data } = await api.post(`${base}/registrations/${reg.id}/${action}`, body);
      if (action === 'confirm') {
        toast.success(data?.pool_entry_created
          ? 'Confirmed — added to the player pool'
          : 'Confirmed (already in the pool)');
      } else if (action === 'cancel' && data?.needs_refund) {
        toast('Cancelled. This registration was paid — use Refund to return the money.', { icon: '⚠️' });
      } else {
        toast.success('Done');
      }
      load();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Action failed');
      return false;
    } finally {
      setBusyId(null);
    }
  }

  function markPaid(reg) {
    const method = window.prompt('Payment method: cash, etransfer, cheque, or other', 'etransfer');
    if (!method) return;
    const m = method.trim().toLowerCase();
    if (!['cash', 'etransfer', 'cheque', 'other'].includes(m)) {
      toast.error('Method must be cash, etransfer, cheque, or other');
      return;
    }
    const note = window.prompt('Note (optional — e.g. reference number)') || '';
    act(reg, 'mark-paid', { method: m, note });
  }
  function refund(reg) {
    const note = window.prompt(
      reg.has_stripe_payment
        ? 'Refund via Stripe (money returns to the payer). Note (optional):'
        : 'Record a refund (you return the money outside Game Day). Note (optional):'
    );
    if (note === null) return;
    act(reg, 'refund', { note });
  }
  function cancel(reg) {
    const reason = window.prompt('Cancel this registration? Reason (optional):');
    if (reason === null) return;
    act(reg, 'cancel', { reason });
  }

  // 2026-09-28 — spreadsheet of every submission (answers, waivers,
  // payments). Fetched through the api client so the auth header and
  // token refresh apply; the browser then saves the blob.
  async function exportCsv() {
    setExporting(true);
    try {
      const res = await api.get(`${base}/export.csv`, { responseType: 'blob', timeout: 60000 });
      const cd = res.headers?.['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(cd);
      const name = m ? m[1] : 'registrations.csv';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  }

  const fmtFee = (r) => r.fee_total_cents
    ? `$${(r.fee_total_cents / 100).toFixed(2)}` : '—';
  const needsAction = (r) =>
    (r.status === 'paid') ||
    (r.status === 'submitted') ||
    (r.parent_consent_required && !r.parent_consent_confirmed_at && !['cancelled', 'refunded'].includes(r.status));

  const summary = useMemo(() => {
    const out = { total: 0, by: {}, collected: 0, outstanding: 0, action: 0 };
    for (const r of regs || []) {
      out.total += 1;
      out.by[r.status] = (out.by[r.status] || 0) + 1;
      if (r.paid_at && !r.refunded_at) out.collected += r.fee_total_cents || 0;
      if (r.status === 'awaiting_payment') out.outstanding += r.fee_total_cents || 0;
      if (needsAction(r)) out.action += 1;
    }
    return out;
  }, [regs]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (regs || []).filter((r) => {
      if (filter === 'action' && !needsAction(r)) return false;
      if (!['all', 'action'].includes(filter) && r.status !== filter) return false;
      if (needle && !(`${r.player_name} ${r.submitted_by_name} ${r.player_uid || ''}`.toLowerCase().includes(needle))) return false;
      return true;
    });
  }, [regs, filter, q]);

  const chips = [
    { id: 'all', label: `All ${summary.total}` },
    { id: 'action', label: `Needs action ${summary.action}` },
    ...['awaiting_payment', 'submitted', 'paid', 'confirmed', 'cancelled', 'refunded']
      .filter((st) => summary.by[st])
      .map((st) => ({ id: st, label: `${st.replace('_', ' ')} ${summary.by[st]}` })),
  ];

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <h2 className="section-title" style={{ fontSize: 16, margin: 0, flex: 1 }}>Submissions</h2>
        {regs?.length > 0 && (
          <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={exportCsv} disabled={exporting}>
            <Download size={13} /> {exporting ? 'Exporting…' : 'Export spreadsheet (CSV)'}
          </button>
        )}
      </div>
      {regs?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 12 }}>
          <SummaryTile label="Registered" value={summary.total - (summary.by.cancelled || 0) - (summary.by.refunded || 0)} />
          <SummaryTile label="Confirmed" value={summary.by.confirmed || 0} color="var(--goal, #2ecc71)" />
          <SummaryTile label="Needs action" value={summary.action} color="var(--puck, #f5a623)" />
          <SummaryTile label="Collected" value={`$${(summary.collected / 100).toFixed(2)}`} />
          <SummaryTile label="Outstanding" value={`$${(summary.outstanding / 100).toFixed(2)}`} />
        </div>
      )}
      {regs === null && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Loading…</div>}
      {regs?.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
          No registrations yet. Share the public link to get sign-ups.
        </div>
      )}
      {regs?.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            {chips.map((c) => (
              <button key={c.id} type="button" onClick={() => setFilter(c.id)} style={{
                fontSize: 12, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', textTransform: 'capitalize',
                border: `1px solid ${filter === c.id ? 'var(--ice)' : 'var(--border)'}`,
                background: filter === c.id ? 'var(--ice-a10, rgba(0,212,255,0.1))' : 'transparent',
                color: filter === c.id ? 'var(--ice)' : 'var(--text-2)', fontWeight: 600,
              }}>{c.label}</button>
            ))}
            <div style={{ position: 'relative', marginLeft: 'auto', minWidth: 180 }}>
              <Search size={13} style={{ position: 'absolute', left: 9, top: 9, color: 'var(--text-2)' }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or ID"
                     style={{ ...inputStyle, paddingLeft: 28, fontSize: 13, marginBottom: 0 }} />
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-2)' }}>
                  <th style={thStyle}>Player</th>
                  <th style={thStyle}>Submitted by</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Fee</th>
                  <th style={thStyle}>Paid</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(r => {
                  const busy = busyId === r.id;
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={tdStyle}>
                        <button type="button" onClick={() => setViewId(r.id)} style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
                          color: 'var(--text)', fontWeight: 600, fontSize: 13,
                        }}>{r.player_name}</button>
                        <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                          {r.player_uid || ''}{r.submitted_at ? ` · ${new Date(r.submitted_at).toLocaleDateString()}` : ''}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {r.submitted_by_name}
                        {r.submitting_user_relationship && r.submitting_user_relationship !== 'self' && (
                          <span style={{ color: 'var(--text-2)' }}> ({r.submitting_user_relationship})</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <StatusChip status={r.status} />
                        {/* Phase E — consent gate indicator. Confirm is
                            blocked server-side until this clears. */}
                        {r.parent_consent_required && !r.parent_consent_confirmed_at
                          && !['cancelled', 'refunded'].includes(r.status) && (
                          <div style={{ fontSize: 10, color: 'var(--puck, #f5a623)', marginTop: 3 }}
                               title="A minor's registration can't be confirmed until the guardian clicks the consent link emailed to them.">
                            ⏳ guardian consent pending
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>{fmtFee(r)}</td>
                      <td style={tdStyle}>
                        {r.paid_at
                          ? <>
                              {new Date(r.paid_at).toLocaleDateString()}
                              <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{r.paid_method}{r.paid_note ? ` · ${r.paid_note}` : ''}</div>
                            </>
                          : '—'}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <button className="btn-ghost" style={{ ...actionBtnStyle, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                onClick={() => setViewId(r.id)}>
                          <Eye size={12} /> View
                        </button>
                        {canFinance && ['awaiting_payment', 'submitted'].includes(r.status) && r.fee_total_cents > 0 && (
                          <button className="btn-ghost" style={actionBtnStyle} disabled={busy} onClick={() => markPaid(r)}>Mark paid</button>
                        )}
                        {canEdit && ['submitted', 'paid'].includes(r.status) && (
                          <button className="btn-ice" style={actionBtnStyle} disabled={busy} onClick={() => act(r, 'confirm')}>Confirm</button>
                        )}
                        {canFinance && r.paid_at && !r.refunded_at && (
                          <button className="btn-ghost" style={actionBtnStyle} disabled={busy} onClick={() => refund(r)}>Refund</button>
                        )}
                        {canEdit && !['cancelled', 'refunded'].includes(r.status) && (
                          <button className="btn-ghost" style={{ ...actionBtnStyle, color: 'var(--penalty, #ff4444)' }} disabled={busy} onClick={() => cancel(r)}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {shown.length === 0 && (
                  <tr><td colSpan={6} style={{ ...tdStyle, color: 'var(--text-2)' }}>Nothing matches this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {viewId && (
        <RegistrationDetailModal
          base={base}
          registrationId={viewId}
          seasonName={seasonName}
          configName={configName}
          leagueId={leagueId}
          canEdit={canEdit}
          onConfirm={async (reg) => { if (await act(reg, 'confirm')) setViewId(null); }}
          onClose={() => setViewId(null)}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, color }) {
  return (
    <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--surface-2, rgba(255,255,255,0.04))', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: color || 'var(--text)', marginTop: 2 }}>{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 2026-09-28 — one registration in full: answers, signed waivers,
// guardian consent, payment trail; printable signed-waiver record.
// ─────────────────────────────────────────────────────────────────
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, {
  year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');
const fmtMoney = (cents, currency = 'CAD') => {
  if (cents == null) return '—';
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100); }
  catch { return `$${(cents / 100).toFixed(2)}`; }
};
function answerText(a) {
  if (a.value == null || a.value === '') return '—';
  if (Array.isArray(a.value)) return a.value.join(', ');
  if (typeof a.value === 'boolean') return a.value ? 'Yes' : 'No';
  return String(a.value);
}

function RegistrationDetailModal({ base, registrationId, seasonName, configName, leagueId, canEdit, onConfirm, onClose }) {
  const [reg, setReg] = useState(null);
  const [error, setError] = useState(null);
  const [leagueName, setLeagueName] = useState('');
  useEffect(() => {
    let cancelled = false;
    api.get(`${base}/registrations/${registrationId}`)
      .then(({ data }) => { if (!cancelled) setReg(data?.registration || null); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.error || 'Could not load this registration'); });
    api.get(`/leagues/${leagueId}`)
      .then(({ data }) => { if (!cancelled) setLeagueName(data?.league?.name || ''); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [base, registrationId, leagueId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const section = (title, children) => (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
  const row = (k, v) => (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
      <div style={{ width: 170, flexShrink: 0, color: 'var(--text-2)' }}>{k}</div>
      <div style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{v}</div>
    </div>
  );

  // Portal to <body>: on phones every [class*=card] gets `contain:
  // layout`, which would trap a fixed overlay inside the list card.
  return createPortal(
    <div role="dialog" aria-modal="true" onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{
        width: '100%', maxWidth: 760, position: 'relative', padding: 22,
      }}>
        <button type="button" onClick={onClose} aria-label="Close" style={{
          position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)',
        }}><X size={20} /></button>
        {!reg && !error && <div>Loading…</div>}
        {error && <div style={{ color: 'var(--penalty, #ff4444)' }}>{error}</div>}
        {reg && (
          <>
            <div style={{ paddingRight: 36 }}>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{[leagueName, seasonName, configName].filter(Boolean).join(' · ')}</div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{reg.player?.name || 'Registration'}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                <StatusChip status={reg.status} />
                {reg.player?.player_uid && <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Player ID {reg.player.player_uid}</span>}
                {reg.team && <span style={{ fontSize: 12, color: 'var(--text-2)' }}>· {reg.team.name}</span>}
                {reg.pool_status && <span style={{ fontSize: 12, color: 'var(--text-2)' }}>· pool: {reg.pool_status}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      onClick={() => printRegistrationRecord(reg, { leagueName, seasonName, configName })}>
                <Printer size={13} /> Print / save signed record
              </button>
              {canEdit && ['submitted', 'paid'].includes(reg.status) && (
                <button className="btn-ice" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => onConfirm(reg)}>
                  Confirm registration
                </button>
              )}
            </div>

            {section('Player', <>
              {row('Name', reg.player?.name || '—')}
              {row('Date of birth', reg.player?.date_of_birth || '—')}
              {row('Position', reg.player?.position || '—')}
              {row('Game Day account', reg.player?.has_account ? 'Linked' : 'Not linked yet')}
            </>)}

            {section('Submitted by', <>
              {row('Name', `${reg.submitted_by?.name || '—'}${reg.relationship && reg.relationship !== 'self' ? ` (${reg.relationship})` : ' (the player)'}`)}
              {row('Email', reg.submitted_by?.email || '—')}
              {row('Phone', reg.submitted_by?.phone || '—')}
              {row('Submitted', fmtDateTime(reg.submitted_at))}
            </>)}

            {reg.guardian_consent?.required && section('Parent / guardian consent', <>
              {row('Status', reg.guardian_consent.confirmed_at
                ? <span style={{ color: 'var(--goal, #2ecc71)' }}>Confirmed {fmtDateTime(reg.guardian_consent.confirmed_at)}</span>
                : <span style={{ color: 'var(--puck, #f5a623)' }}>Waiting for the guardian to click the consent link</span>)}
              {reg.guardian_consent.guardian_name && row('Guardian', `${reg.guardian_consent.guardian_name}${reg.guardian_consent.relationship ? ` (${reg.guardian_consent.relationship})` : ''}`)}
              {reg.guardian_consent.guardian_email && row('Guardian email', reg.guardian_consent.guardian_email)}
            </>)}

            {section('Form answers', reg.answers.length === 0
              ? <div style={{ fontSize: 13, color: 'var(--text-2)' }}>This form has no custom questions.</div>
              : reg.answers.map((a) => row(
                  <span>{a.label}{a.sensitive && <Lock size={11} style={{ marginLeft: 5, verticalAlign: -1 }} title="Stored encrypted" />}</span>,
                  answerText(a),
                )))}

            {section('Waivers', <>
              {reg.waivers.length === 0 && reg.waivers_not_signed.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--text-2)' }}>No waivers on this registration.</div>
              )}
              {reg.waivers.map((w) => (
                <div key={w.section_id} style={{ padding: 12, borderRadius: 10, border: '1px solid var(--border)', marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700 }}>{w.title}</div>
                    <span style={{ fontSize: 11, color: 'var(--text-2)' }}>version {w.version}</span>
                    {!w.is_current_version && (
                      <span style={{ fontSize: 11, color: 'var(--puck, #f5a623)' }} title="The league has since published a newer version of this waiver. This person signed the older text, which is kept exactly as signed.">
                        · older version
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 6 }}>
                    ✅ Signed “<strong>{w.signature}</strong>” on {fmtDateTime(w.acknowledged_at)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4 }}>
                    IP {w.ip_address || '—'} · {w.user_agent ? w.user_agent.slice(0, 90) : 'device not recorded'}
                  </div>
                  {w.acknowledgment_text && (
                    <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6, fontStyle: 'italic' }}>{w.acknowledgment_text}</div>
                  )}
                </div>
              ))}
              {reg.waivers_not_signed.map((w) => (
                <div key={w.section_id} style={{ padding: 10, borderRadius: 10, border: '1px dashed var(--border)', marginBottom: 8, fontSize: 13, color: 'var(--text-2)' }}>
                  Not signed: <strong>{w.title}</strong> (v{w.version}){w.required ? ' — required for new sign-ups' : ' — optional'}
                </div>
              ))}
            </>)}

            {section('Payment', <>
              {row('Fee', fmtMoney(reg.fee_total_cents, reg.currency || 'CAD'))}
              {row('Paid', reg.paid_at ? `${fmtDateTime(reg.paid_at)} · ${reg.paid_method || ''}${reg.paid_note ? ` · ${reg.paid_note}` : ''}` : '—')}
              {reg.refunded_at && row('Refunded', `${fmtDateTime(reg.refunded_at)}${reg.refund_note ? ` · ${reg.refund_note}` : ''}`)}
              {row('Confirmed', fmtDateTime(reg.confirmed_at))}
              {reg.cancelled_at && row('Cancelled', `${fmtDateTime(reg.cancelled_at)}${reg.cancellation_reason ? ` · ${reg.cancellation_reason}` : ''}`)}
            </>)}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Opens a clean, printable page (browser "Save as PDF" works too) with
// the registration and the FULL text of every waiver exactly as signed.
function escHtml(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function mdToSimpleHtml(md) {
  // Deliberately tiny: escape everything, keep paragraphs, line breaks,
  // bullets and **bold**. No links or raw HTML reach the print page.
  const esc = escHtml(md || '');
  return esc.split(/\n{2,}/).map((para) => {
    const lines = para.split('\n');
    if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
      return `<ul>${lines.map((l) => `<li>${l.replace(/^\s*[-*]\s+/, '')}</li>`).join('')}</ul>`;
    }
    const heading = /^#{1,6}\s+(.*)$/.exec(para);
    if (heading) return `<h4>${heading[1]}</h4>`;
    return `<p>${lines.join('<br>')}</p>`;
  }).join('')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*\w])\*(?!\s)([^*<>]+?)\*(?![*\w])/g, '$1<em>$2</em>');
}
function printRegistrationRecord(reg, { leagueName, seasonName, configName }) {
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) { toast.error('Allow pop-ups to print the record'); return; }
  const r = (k, v) => `<tr><th>${escHtml(k)}</th><td>${escHtml(v)}</td></tr>`;
  const answers = reg.answers.map((a) => r(a.label, answerText(a))).join('');
  const waivers = reg.waivers.map((wv) => `
    <section class="waiver">
      <h3>${escHtml(wv.title)} <small>version ${escHtml(wv.version)}${wv.is_current_version ? '' : ' (older version)'}</small></h3>
      <div class="body">${mdToSimpleHtml(wv.body_markdown)}</div>
      ${wv.acknowledgment_text ? `<p class="ack">☑ ${escHtml(wv.acknowledgment_text)}</p>` : ''}
      <table class="sig">
        ${r('Signed by (typed signature)', wv.signature)}
        ${r('Signed at', fmtDateTime(wv.acknowledged_at))}
        ${r('IP address', wv.ip_address || '—')}
        ${r('Device', wv.user_agent || '—')}
      </table>
    </section>`).join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Registration record — ${escHtml(reg.player?.name || '')}</title>
  <style>
    body{font:14px/1.5 -apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#111;margin:32px auto;max-width:760px;padding:0 20px}
    h1{font-size:22px;margin:0 0 4px} h2{font-size:15px;margin:26px 0 8px;border-bottom:2px solid #111;padding-bottom:4px}
    h3{font-size:15px;margin:0 0 6px} h3 small{font-weight:400;color:#555} .muted{color:#555}
    table{border-collapse:collapse;width:100%} th{width:190px;text-align:left;color:#444;font-weight:600;vertical-align:top;padding:5px 8px 5px 0}
    td{padding:5px 0;border-bottom:1px solid #ddd;vertical-align:top;word-break:break-word}
    .waiver{border:1px solid #bbb;border-radius:8px;padding:14px 16px;margin:12px 0;page-break-inside:avoid}
    .waiver .body{font-size:13px;margin:8px 0} .ack{font-weight:600} .sig{margin-top:8px}
    .foot{margin-top:28px;font-size:11px;color:#666}
    @media print{body{margin:0 auto}.noprint{display:none}}
  </style></head><body>
  <p class="noprint"><button onclick="window.print()">Print / Save as PDF</button></p>
  <div class="muted">${escHtml([leagueName, seasonName, configName].filter(Boolean).join(' · '))}</div>
  <h1>Registration record — ${escHtml(reg.player?.name || '')}</h1>
  <div class="muted">Status: ${escHtml((reg.status || '').replace('_', ' '))} · Registration ID ${escHtml(reg.id)}</div>
  <h2>Player</h2><table>
    ${r('Name', reg.player?.name || '—')}${r('Player ID', reg.player?.player_uid || '—')}
    ${r('Date of birth', reg.player?.date_of_birth || '—')}${r('Position', reg.player?.position || '—')}
  </table>
  <h2>Submitted by</h2><table>
    ${r('Name', reg.submitted_by?.name || '—')}${r('Relationship', ({ self: 'The player', parent: 'Parent', guardian: 'Guardian', other: 'Other' })[reg.relationship || 'self'] || reg.relationship)}
    ${r('Email', reg.submitted_by?.email || '—')}${r('Phone', reg.submitted_by?.phone || '—')}
    ${r('Submitted', fmtDateTime(reg.submitted_at))}
  </table>
  ${reg.guardian_consent?.required ? `<h2>Parent / guardian consent</h2><table>
    ${r('Consent', reg.guardian_consent.confirmed_at ? `Confirmed ${fmtDateTime(reg.guardian_consent.confirmed_at)}` : 'Pending')}
    ${r('Guardian', reg.guardian_consent.guardian_name || '—')}${r('Guardian email', reg.guardian_consent.guardian_email || '—')}
  </table>` : ''}
  <h2>Form answers</h2><table>${answers || r('—', 'No custom questions')}</table>
  <h2>Signed waivers</h2>${waivers || '<p class="muted">No waivers signed.</p>'}
  <h2>Payment</h2><table>
    ${r('Fee', fmtMoney(reg.fee_total_cents, reg.currency || 'CAD'))}
    ${r('Paid', reg.paid_at ? `${fmtDateTime(reg.paid_at)} · ${reg.paid_method || ''}${reg.paid_note ? ` · ${reg.paid_note}` : ''}` : '—')}
    ${reg.refunded_at ? r('Refunded', fmtDateTime(reg.refunded_at)) : ''}
    ${r('Confirmed', fmtDateTime(reg.confirmed_at))}
  </table>
  <div class="foot">Generated from Game Day on ${escHtml(new Date().toLocaleString())}. Waiver text is reproduced exactly as it read when it was signed.</div>
  </body></html>`;
  w.document.open();
  w.document.write(html);
  w.document.close();
}

function StatusChip({ status }) {
  const palette = {
    submitted:        { bg: 'rgba(0,212,255,0.10)',  fg: 'var(--ice)' },
    awaiting_payment: { bg: 'rgba(255,170,0,0.12)',  fg: 'var(--puck, #f5a623)' },
    paid:             { bg: 'rgba(46,204,113,0.12)', fg: 'var(--goal, #2ecc71)' },
    confirmed:        { bg: 'rgba(46,204,113,0.18)', fg: 'var(--goal, #2ecc71)' },
    cancelled:        { bg: 'rgba(255,68,68,0.10)',  fg: 'var(--penalty, #ff4444)' },
    refunded:         { bg: 'rgba(178,132,255,0.12)', fg: '#b284ff' },
  }[status] || { bg: 'var(--bg-2)', fg: 'var(--text-2)' };
  return (
    <span style={{
      background: palette.bg, color: palette.fg,
      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
    }}>
      {status.replace('_', ' ')}
    </span>
  );
}

const thStyle = { padding: '6px 8px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' };
const tdStyle = { padding: '8px', verticalAlign: 'top' };
const actionBtnStyle = { fontSize: 11, padding: '4px 8px', marginRight: 6 };

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>
        {label}
      </label>
      {children}
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
}

function deriveKey(label) {
  return String(label || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 64);
}

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 12px',
  color: 'var(--text)',
  fontSize: 13,
  outline: 'none',
};

const selectStyle = {
  ...inputStyle,
  cursor: 'pointer',
};

const fieldRowStyle = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: 12,
  background: 'var(--surface-2)',
  borderRadius: 10,
  border: '1px solid var(--border)',
};

const miniLabelStyle = {
  display: 'block',
  fontSize: 11, fontWeight: 600, color: 'var(--text-2)',
  marginBottom: 4,
  textTransform: 'uppercase', letterSpacing: '0.06em',
};

const miniBtnStyle = (enabled) => ({
  background: 'transparent',
  border: 'none',
  padding: '2px 4px',
  fontSize: 10,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 0.7 : 0.2,
  color: 'var(--text-2)',
});

const codeStyle = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  background: 'var(--surface)',
  padding: '1px 5px',
  borderRadius: 4,
};
